import { Link } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { SITE } from "../data/site";
import { MaskedLine, FadeUp } from "./motion";
import { Eyebrow } from "./Reveal";

const STRINGS = {
    en: {
        eyebrow: "Festival Season 2026",
        line1: "THE BIGGEST SELLING WINDOW",
        line2: "OF THE YEAR STARTS",
        line3: "SEPTEMBER 14.",
        sub: "Ganesh Chaturthi. Navratri. Diwali. Customers will spend — the only question is who they spend with. Festival campaigns need three to four weeks of runway: visibility, offers, retail presence and follow-up systems built before the rush, not during it.",
        timelineTitle: "The runway",
        events: [
            { date: "Sep 14", name: "Ganesh Chaturthi" },
            { date: "Oct 11–20", name: "Navratri · Dussehra" },
            { date: "Nov 8", name: "Diwali" },
        ],
        note: "Festival campaigns launched late are just discounts. Launched early, they're market capture.",
        cta: "Boost My Festival Sales",
        whatsapp: "Plan on WhatsApp",
    },
    hi: {
        eyebrow: "फ़ेस्टिव सीज़न 2026",
        line1: "साल का सबसे बड़ा",
        line2: "बिक्री-मौसम शुरू होता है",
        line3: "14 सितंबर से।",
        sub: "गणेश चतुर्थी। नवरात्रि। दिवाली। ग्राहक खर्च करेंगे — सवाल बस इतना है कि किसके साथ। फ़ेस्टिव कैंपेन को तीन-चार हफ़्ते की तैयारी चाहिए: भीड़ के दौरान नहीं, उससे पहले बनी हुई विज़िबिलिटी, ऑफर, रिटेल प्रेज़ेंस और फ़ॉलो-अप सिस्टम।",
        timelineTitle: "तैयारी की तारीख़ें",
        events: [
            { date: "14 सितंबर", name: "गणेश चतुर्थी" },
            { date: "11–20 अक्टूबर", name: "नवरात्रि · दशहरा" },
            { date: "8 नवंबर", name: "दिवाली" },
        ],
        note: "देर से चलाए गए फ़ेस्टिव कैंपेन महज़ डिस्काउंट होते हैं। समय पर चलाए गए — बाज़ार पर क़ब्ज़ा।",
        cta: "फ़ेस्टिव सेल्स बढ़ाएँ",
        whatsapp: "WhatsApp पर प्लान करें",
    },
};

const FestivalBanner = ({ lang = "en" }) => {
    const t = STRINGS[lang];
    const contactPath = lang === "hi" ? "/hi/contact?intent=festival" : "/contact?intent=festival";

    return (
        <section
            className="relative bg-[#0D131F] text-white overflow-hidden"
            data-testid="festival-banner"
            style={{
                backgroundImage:
                    "radial-gradient(ellipse at 18% 0%, rgba(197,160,89,0.14), transparent 55%)",
            }}
        >
            <div className="hero-noise" />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28 grid lg:grid-cols-12 gap-14 items-center">
                <div className="lg:col-span-7">
                    <FadeUp y={14}>
                        <Eyebrow dark>{t.eyebrow}</Eyebrow>
                    </FadeUp>
                    <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.06] mt-6">
                        <MaskedLine scroll delay={0.05}>{t.line1}</MaskedLine>
                        <MaskedLine scroll delay={0.15}>{t.line2}</MaskedLine>
                        <MaskedLine scroll delay={0.25} className="text-[#C5A059]">{t.line3}</MaskedLine>
                    </h2>
                    <FadeUp delay={0.35}>
                        <p className="mt-6 text-slate-400 text-base sm:text-lg leading-relaxed max-w-2xl">
                            {t.sub}
                        </p>
                        <div className="mt-9 flex flex-wrap gap-4">
                            <Link
                                to={contactPath}
                                data-testid="festival-cta-button"
                                className="btn-sharp bg-[#C5A059] text-[#0D131F] text-xs font-bold tracking-[0.15em] uppercase px-8 py-4"
                            >
                                {t.cta}
                            </Link>
                            <a
                                href={SITE.whatsappPrefill}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="festival-whatsapp-link"
                                className="btn-sharp flex items-center gap-2 border border-white/25 text-white text-xs font-bold tracking-[0.15em] uppercase px-8 py-4 hover:border-[#25D366] hover:text-[#25D366]"
                            >
                                <MessageCircle className="h-4 w-4" /> {t.whatsapp}
                            </a>
                        </div>
                    </FadeUp>
                </div>
                <div className="lg:col-span-5">
                    <FadeUp delay={0.3} y={40}>
                        <div className="relative border border-white/10 bg-white/[0.03] backdrop-blur-sm p-8 sm:p-10">
                            <span className="absolute -top-1.5 -left-1.5 h-3 w-3 bg-[#C5A059]" />
                            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#C5A059]">
                                {t.timelineTitle}
                            </p>
                            <div className="mt-7 space-y-0">
                                {t.events.map((e, i) => (
                                    <div key={e.name} className="relative flex items-center gap-5 pb-7 last:pb-0">
                                        {i < t.events.length - 1 && (
                                            <span className="absolute left-[5px] top-4 bottom-0 w-px bg-[#C5A059]/30" />
                                        )}
                                        <span className="relative h-2.5 w-2.5 bg-[#C5A059] shrink-0" />
                                        <div className="flex items-baseline gap-3 flex-wrap">
                                            <span className="font-display font-bold text-white text-lg">{e.name}</span>
                                            <span className="text-xs tracking-[0.15em] uppercase text-slate-400">{e.date}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="mt-8 pt-6 border-t border-white/10 font-serif-accent italic text-sm sm:text-base text-slate-300 leading-relaxed">
                                {t.note}
                            </p>
                        </div>
                    </FadeUp>
                </div>
            </div>
        </section>
    );
};

export default FestivalBanner;
